import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { proDeploymentApi } from '../api/pro-deployment.adapter';
import type { CurrentConfigQuery, ItemPoolQuery } from '../types/pro-deployment.types';

export const proDeploymentQueryKeys = {
  all: ['pro-deployment'] as const,
  currentConfig: (query?: CurrentConfigQuery) =>
    [...proDeploymentQueryKeys.all, 'current-config', query ?? {}] as const,
  diseaseOptions: () => [...proDeploymentQueryKeys.all, 'disease-options'] as const,
  itemPool: (disease: string, freq: string, weekdays: string) =>
    [...proDeploymentQueryKeys.all, 'item-pool', disease, freq, weekdays] as const,
  history: (limit: number) => [...proDeploymentQueryKeys.all, 'history', limit] as const,
  resetCount: (disease: string) => [...proDeploymentQueryKeys.all, 'reset-count', disease] as const,
};

// PRD 06 §3 — single bootstrap call for the configuration being edited. Keep the
// previous response visible while a new disease row loads (the query key changes
// on disease change) so the page doesn't flash the full-page loading card on
// every filter change.
//
// FEATURE_025 (PRO Distribution Management) — this response is the page's
// bootstrap payload: the stat cards (항목 수 / 문항 수) are derived from its
// `modules` + `items`, so anything upstream that changes the item pool (item
// add/edit/delete/publish, question edits, module changes) makes a cached copy
// wrong. The app-wide default keeps a query fresh for 30s and does not refetch
// on mount, so re-entering the page within that window served stale totals.
// Always refetch on mount instead of relying on every upstream feature
// remembering to invalidate this key; `placeholderData` keeps the old response
// on screen so there is no loading flash during the refetch.
export function useDeploymentCurrentConfigQuery(query?: CurrentConfigQuery) {
  return useQuery({
    queryKey: proDeploymentQueryKeys.currentConfig(query),
    queryFn: () => proDeploymentApi.getCurrentConfig(query),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });
}

// PRD 06 §3.2 — hierarchical disease select options. Effectively static
// during a session; cache indefinitely.
export function useDiseaseOptionsQuery() {
  return useQuery({
    queryKey: proDeploymentQueryKeys.diseaseOptions(),
    queryFn: () => proDeploymentApi.listDiseaseOptions(),
    staleTime: Infinity,
  });
}

// PRD 06 §3.3 — eligible items for the add-popover. Pass `enabled: false`
// (the default until `query` is provided) so we only hit the adapter when
// the popover actually opens.
export function useItemPoolQuery(query: ItemPoolQuery | null) {
  return useQuery({
    queryKey: query
      ? proDeploymentQueryKeys.itemPool(query.disease, query.freq, query.weekdays.join(','))
      : ['pro-deployment', 'item-pool', 'disabled'],
    queryFn: () => proDeploymentApi.listItemPool(query as ItemPoolQuery),
    enabled: query !== null,
  });
}

// PRD 06 §3.5 / PRD 21 §10.5 — recent deploy history. Server caps at 50;
// FE renders the latest `limit` (default 10 per spec).
// FEATURE_025 — the drafts a deploy of `disease` would reset, for the deploy confirmation. Read only while the
// confirmation is open (`enabled`), and always fresh: patients keep saving drafts while the page is open.
export function useResetCountQuery(disease: string, enabled: boolean) {
  return useQuery({
    queryKey: proDeploymentQueryKeys.resetCount(disease),
    queryFn: () => proDeploymentApi.getResetCount(disease),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDeployHistoryQuery(limit: number = 10) {
  return useQuery({
    queryKey: proDeploymentQueryKeys.history(limit),
    queryFn: () => proDeploymentApi.listHistory(limit),
  });
}
